import { InformBox } from "@/components/inform-box/InformBox.tsx";
import {
  Skeleton,
  useModals,
  Modal,
  ModalProps,
  Button,
  ButtonLink,
  FontAwesomeIcon,
  Input,
  Stack,
  Switch,
  Text,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  useTabByTypeAndId,
  useTableCheckSlug,
  useTableSave,
} from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCFEntityTypes } from "@/types/custom-field.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EmployeeCustomTableEditModalProps extends ModalProps {
  tabId: number;
}

export function EmployeeCustomTableModal({ tabId, ...props }: EmployeeCustomTableEditModalProps) {
  const { id } = props;
  const { close } = useModals();
  const { t } = useTranslation();
  const [checkedTitle, setCheckedTitle] = useState<string>("");
  const EmployeeCustomTableSchemaObject = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    is_historical: z.boolean().optional(),
  });

  const { canIEdit } = usePermissions();

  const tab = useTabByTypeAndId(tabId, ApiCFEntityTypes.employee);

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(EmployeeCustomTableSchemaObject),
    defaultValues: {
      title: "",
      is_historical: false,
    },
  });
  const tableTitle = watch("title");
  const { mutateAsync: save, isPending: isSavePending } = useTableSave(control);
  const { data: checkResult } = useTableCheckSlug(checkedTitle);
  const slugIsOk = checkResult?.success ?? true;

  const onSubmit = handleSubmit(async (data) => {
    if (isSavePending || !slugIsOk || !tab) return;

    const tableSaved = await save({
      id: 0,
      payload: {
        title: data.title,
        tab_type: tab.type,
        is_historical: data.is_historical ?? false,
      },
    });

    if (tableSaved) {
      analyticsService.trackEvent(analyticEvents.customFields.tableCreated, {
        [analyticProperties.id]: tableSaved.id,
      });

      close(id);
    }
  });

  useDebounceUpdate({
    data: tableTitle,
    timeout: 300,
    execute: setCheckedTitle,
  });

  if (!tab) {
    return (
      <Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Modal
      {...props}
      title={t("custom_fields:create_new_table")}
      withCloser={true}
      actions={
        <Stack gap={"md"}>
          {tableTitle?.length > 0 && !slugIsOk ? (
            <Stack className={"h-14"} justify={"center"}>
              <Text className={"text-danger"}>{t("custom_fields:change_table_name")}</Text>
            </Stack>
          ) : (
            <></>
          )}
          <ServerErrorField errors={errors} />

          <Button size={"lg"} variant={"primary"} onClick={onSubmit} isLoading={isSavePending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Stack>
            <Input
              {...register("title")}
              label={t("common:title")}
              error={errors.title?.message}
              required
            />
          </Stack>
          <Controller
            render={({ field: { onChange, value } }) => (
              <Switch
                onChange={onChange}
                checked={value}
                title={t("custom_fields:historical_title")}
                description={t("custom_fields:historical_description")}
              />
            )}
            name={"is_historical"}
            control={control}
          />
          <InformBox
            emoji={":bulb:"}
            title={t("custom_fields:just_reminder")}
            description={t("custom_fields:information_about_permissions", {
              this_entity: t("custom_fields:this_table"),
              entity_be_visible: t("custom_fields:table_be_visible"),
            })}
            actionsSlot={
              canIEdit(ApiPermission.settings_roles_permissions) ? (
                <Stack items={"start"}>
                  <ButtonLink
                    href="/settings/roles-and-permissions"
                    target={"_blank"}
                    size={"sm"}
                    variant={"secondary"}
                    rightIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />}
                  >
                    {t("custom_fields:open_role_permissions")}
                  </ButtonLink>
                </Stack>
              ) : (
                <></>
              )
            }
          />
        </Stack>
      </form>
    </Modal>
  );
}
